import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-session-list.css?inline';
import { fillId, formatUtc, parseJsonArray } from '../../lib/portal-data.js';
import '../pk-button/pk-button.js';
import '../pk-status-pill/pk-status-pill.js';

/** One API session (contract `Session`, `GET /me/sessions`). */
export interface PkSessionRecord {
    id: string;
    deviceLabel?: string | null;
    createdFromIp?: string | null;
    createdAtUtc?: string | null;
    lastUsedAtUtc?: string | null;
    expiresAtUtc?: string | null;
    isCurrentSession?: boolean;
}

/**
 * `<pk-session-list>` — the signed-in user's sessions: device, IP, last used, a marker on the current
 * one, a Revoke button per other session and "Sign out everywhere".
 *
 * Every action is a plain form POST (no fetch), so a server-rendered page works without JavaScript
 * glue: the CMS controller revokes and redirects back. The current session has no Revoke button —
 * signing out is how you end it.
 *
 * Attributes:
 *   - `sessions`          — JSON array of sessions (or set the `sessions` property).
 *   - `revoke-action`     — POST URL template for one session; `{id}` is replaced
 *                           (default `/portal/sessions/{id}/revoke`).
 *   - `revoke-all-action` — POST URL for "sign out everywhere" (default `/portal/sessions/revoke-all`).
 *                           Set it to an empty string to hide the button.
 *   - `csrf-token`        — anti-forgery token, written as a hidden input in every form.
 *   - `csrf-name`         — hidden-input name (default `__RequestVerificationToken`).
 *   - `empty-text`        — shown when there are no sessions (default `No active sessions.`).
 *
 * Events:
 *   - `pk-session-revoke` — `CustomEvent<{ id: string | null; all: boolean }>`, cancelable, fired as a
 *     revoke form submits. `preventDefault()` stops the POST (e.g. to confirm first).
 */
export class PkSessionList extends HTMLElement {
    static readonly tagName = 'pk-session-list';

    static get observedAttributes(): string[] {
        return ['sessions', 'revoke-action', 'revoke-all-action', 'csrf-token', 'csrf-name', 'empty-text'];
    }

    private readonly _root: ShadowRoot;
    private _sessions: PkSessionRecord[] | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'sessions') this._sessions = null;
        if (this.isConnected) this._render();
    }

    /** The sessions (property wins over the attribute once set). */
    get sessions(): PkSessionRecord[] {
        return this._sessions ?? parseJsonArray<PkSessionRecord>(this.getAttribute('sessions'));
    }

    set sessions(value: PkSessionRecord[]) {
        this._sessions = Array.isArray(value) ? value : [];
        if (this.isConnected) this._render();
    }

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        const id = form.dataset.sessionId ?? null;
        const all = form.classList.contains('revoke-all');
        const proceed = this.dispatchEvent(new CustomEvent('pk-session-revoke', {
            bubbles: true,
            composed: true,
            cancelable: true,
            detail: { id, all },
        }));
        if (!proceed) e.preventDefault();
    };

    private _csrf(): TemplateResult | string {
        const token = this.getAttribute('csrf-token') ?? '';
        const name = this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
        return token ? html`<input type="hidden" name=${name} .value=${token} />` : '';
    }

    private _row(s: PkSessionRecord, revokeAction: string): TemplateResult {
        const current = s.isCurrentSession === true;
        return html`
            <tr class=${current ? 'current' : ''} data-session-id=${s.id}>
                <td class="device">
                    <span class="device-label">${s.deviceLabel || 'Unknown device'}</span>
                    ${current ? html`<pk-status-pill kind="success" class="current-marker">This device</pk-status-pill>` : ''}
                </td>
                <td class="ip">${s.createdFromIp || '—'}</td>
                <td class="last-used" data-iso=${s.lastUsedAtUtc ?? ''}>${formatUtc(s.lastUsedAtUtc ?? s.createdAtUtc)}</td>
                <td class="created" data-iso=${s.createdAtUtc ?? ''}>${formatUtc(s.createdAtUtc)}</td>
                <td class="actions">
                    ${current ? '' : html`
                        <form class="revoke" method="post" action=${fillId(revokeAction, s.id)} data-session-id=${s.id}>
                            ${this._csrf()}
                            <pk-button variant="secondary" type="submit">Revoke</pk-button>
                        </form>
                    `}
                </td>
            </tr>
        `;
    }

    private template(): TemplateResult {
        const sessions = this.sessions;
        const revokeAction = this.getAttribute('revoke-action') ?? '/portal/sessions/{id}/revoke';
        const revokeAll = this.getAttribute('revoke-all-action') ?? '/portal/sessions/revoke-all';
        const empty = this.getAttribute('empty-text') ?? 'No active sessions.';

        return html`
            <style>${styles}</style>
            ${sessions.length === 0
                ? html`<p class="empty">${empty}</p>`
                : html`
                    <table>
                        <thead>
                            <tr>
                                <th scope="col">Device</th>
                                <th scope="col">IP address</th>
                                <th scope="col">Last used</th>
                                <th scope="col">Signed in</th>
                                <th scope="col"><span class="visually-hidden">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>${sessions.map((s) => this._row(s, revokeAction))}</tbody>
                    </table>
                `}
            ${revokeAll ? html`
                <form class="revoke-all" method="post" action=${revokeAll}>
                    ${this._csrf()}
                    <pk-button variant="danger" type="submit">Sign out everywhere</pk-button>
                </form>
            ` : ''}
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkSessionList.tagName)) {
    customElements.define(PkSessionList.tagName, PkSessionList);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-session-list': PkSessionList;
    }
}
